import { PickerType } from "pages/pickers/types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { PICKERS_STATUS } from "utils/constants";
import { getDifDate } from "utils/date";
import "./TablePickers.scss";
import "./i18n";
import { TableAdminPropsType } from "./types";
import { HeaderType, Table } from "@pickit/pickit-components";
import useTableConfig from "hooks/useTableConfig";

export const TablePickers: React.FC<TableAdminPropsType> = ({
  actualPage,
  pendingUsers,
  testId,
  seeMore,
  collapseTable,
  getMorePickers,
}): JSX.Element => {
  const { t } = useTranslation("tablePickers");
  const { fontSize, rowCount } = useTableConfig(collapseTable);
  const history = useHistory();


  const sharedHeaders: HeaderType<PickerType>[] = [
    {
      key: ["personalData.name", "personalData.surname"],
      label: t("name"),
      testId: "name",
      col: 3,
    },
    {
      key: "personalData.identification.number",
      label: t("identifier"),
      testId: "identification",
      col: 4,
    },
    {
      key: "personalData.email",
      label: t("email"),
      testId: "email",
    },
    {
      key: "vehicle.type",
      label: t("vehicle"),
      col: 3,
      testId: "vehicle",
      textConfig: { formatter: (type) => t(`tablePickers:${type}`) },
    },
  ];

  const wspIcon: HeaderType<PickerType> = {
    label: t("whatsapp"),
    type: "icon",
    iconConfig: {
      name: "whatsapp",
      color: "#25D366",
      disabledCondition: (user) => {
        const phone = user.personalData.phone;
        return !phone.countryNumber || !phone.areaNumber || !phone.number;
      },
      onClick: (user) => {
        const phone = user.personalData.phone;
        const name = user.personalData.name;
        window.open(
          `https://api.whatsapp.com/send/?phone=${phone.countryNumber}${
            phone.areaNumber
          }${phone.number}&text=${t("tablePickers:contactText", {
            name,
          })}&type=phone_number&app_absent=0`
        );
      },
    },
    testId: "phone",
    col: 2,
    align: "center",
  };

  const pendingHeaders: HeaderType<PickerType>[] = [
    ...sharedHeaders,
    {
      key: "registerDatetime",
      label: `${t("pending")}`,
      testId: "register-date",
      col: 3,
      textConfig: {
        formatter: (value: string) =>
          `${getDifDate(value)} ${t(
            `tablePickers:${getDifDate(value) === 1 ? "day" : "days"}`
          )}`,
      },
    },
    {
      type: "tag",
      label: `${t("status")}`,
      key: "status.tag",
      testId: "correction-tag",
      showCondition: (user) => user.status.tag === PICKERS_STATUS.inCorrection,
      col: 2,
      tagConfig: {
        variations: {
          [PICKERS_STATUS.inCorrection]: {
            label: `${t("fixing")}`,
            status: "error",
          },
        },
      },
    },
    wspIcon,
  ];

  const activeHeaders: HeaderType<PickerType>[] = [
    ...sharedHeaders,
    {
      type: "tag",
      key: "status.tag",
      label: `${t("status")}`,
      testId: "status",
      col: 3,
      tagConfig: {
        variations: {
          [PICKERS_STATUS.enabled]: {
            label: t("global:enabled"),
            status: "success",
          },
          [PICKERS_STATUS.disabled]: {
            label: t("global:disabled"),
            status: "error",
          },
          [PICKERS_STATUS.incomplete]: {
            label: t("tablePickers:incomplete"),
            status: "warning",
          },
        },
      },
    },
    wspIcon,
  ];

  const headers: { [key: string]: HeaderType<PickerType>[] } = {
    pending: pendingHeaders,
    pickers: activeHeaders,
  };

  const cols: { [key: string]: number } = {
    pending: 21,
    pickers: 18,
  };

  return (
    <Table<PickerType>
      headers={headers[actualPage]}
      data={pendingUsers}
      testId={testId}
      pagination="view-more-button"
      loadMoreRows={getMorePickers}
      hasMoreRows={seeMore}
      rowCount={rowCount}
      fontSize={fontSize}
      cols={cols[actualPage]}
      onClickRow={(row) => history.push(`pickers/detail/${row.id}?tab=${actualPage}`)}
    />
  );
};
